import { useMemo } from 'react';
import { ProfileName } from '@constants/integrations';
import { LocationIntegrationStatus } from '@constants/companies';
import { POST_CHANNEL, normalizePlatform } from '@constants/social-posts';
import { useLocations } from './useLocations';
import usePermissions from './usePermissions';

// Resolves the profile that labels the post preview (name/@handle) — not an eligibility check.
const isConnectedPostingProfile = (location, profile, platform) => {
    const backedByConnectedIntegration =
        location?.integrations?.some(
            i => i.id === profile.locationIntegrationId && i.status === LocationIntegrationStatus.Connected,
        ) ?? false;
    if (!backedByConnectedIntegration) return false;
    switch (platform) {
        case POST_CHANNEL.GOOGLE:
            return profile.type === ProfileName.GoogleOfficial && profile.isVerified;
        case POST_CHANNEL.FACEBOOK:
        case POST_CHANNEL.FACEBOOK_STORY:
            return profile.type === ProfileName.FacebookOfficial && !profile.facebookParentPageId;
        case POST_CHANNEL.INSTAGRAM:
        case POST_CHANNEL.INSTAGRAM_STORY:
            return profile.type === ProfileName.Instagram;
        case POST_CHANNEL.TIKTOK:
            return profile.type === ProfileName.Tiktok;
        case POST_CHANNEL.X:
            return profile.type === ProfileName.X;
        case POST_CHANNEL.LINKEDIN:
            return profile.type === ProfileName.LinkedIn;
        default:
            return false;
    }
};

export const getConnectedPostingProfile = (location, platform) =>
    (location?.profiles ?? []).find(p => isConnectedPostingProfile(location, p, platform)) ?? null;

// Eligibility is computed server-side (AccountLocationModel.availablePlatforms); consumed, never re-derived.
export const isLocationDirectForPlatform = (location, platform) =>
    (location?.availablePlatforms ?? []).some(name => normalizePlatform(name) === platform);

const PLATFORM_OPTIONS = [
    { value: POST_CHANNEL.GOOGLE, label: 'Google' },
    { value: POST_CHANNEL.FACEBOOK, label: 'Facebook' },
    { value: POST_CHANNEL.FACEBOOK_STORY, label: 'Facebook Story' },
    { value: POST_CHANNEL.INSTAGRAM, label: 'Instagram' },
    { value: POST_CHANNEL.INSTAGRAM_STORY, label: 'Instagram Story' },
    { value: POST_CHANNEL.TIKTOK, label: 'TikTok' },
    { value: POST_CHANNEL.X, label: 'X' },
    { value: POST_CHANNEL.LINKEDIN, label: 'LinkedIn' },
];

export const useSocialPostsPlatforms = () => {
    const { locationsMap } = useLocations();
    const { getWritableLocationIds } = usePermissions();

    const platformOptionsForLocations = locationsIds => {
        const locations = locationsIds?.map(companyId => locationsMap[companyId]).filter(Boolean) ?? [];
        return PLATFORM_OPTIONS.filter(({ value }) =>
            locations.some(location => isLocationDirectForPlatform(location, value)),
        );
    };

    const getPlatformsOptions = locationsIds => platformOptionsForLocations(locationsIds);
    const getConnectedPlatforms = locationsIds => platformOptionsForLocations(locationsIds).map(o => o.value);
    const getAllPlatformsOptions = () => PLATFORM_OPTIONS;

    // Require at least one writable, directly-connected location per platform so the user can't pick a
    // platform that has nowhere to publish.
    const accountPlatformsOptions = useMemo(() => {
        const writableIds = getWritableLocationIds('posts');
        const writableLocations = Object.values(locationsMap).filter(
            loc => writableIds === null || writableIds.has(loc.companyId),
        );
        return PLATFORM_OPTIONS.filter(({ value }) =>
            writableLocations.some(loc => isLocationDirectForPlatform(loc, value)),
        );
    }, [locationsMap, getWritableLocationIds]);

    const getAccountPlatformsOptions = () => accountPlatformsOptions;

    return { getPlatformsOptions, getConnectedPlatforms, getAllPlatformsOptions, getAccountPlatformsOptions };
};
